import { useEffect, useState } from "react";
import { X, Keyboard } from "lucide-react";

const SHORTCUTS = [
    { keys: ["?"], desc: "Open this keyboard help" },
    { keys: ["/"], desc: "Focus calculator search in header" },
    { keys: ["Esc"], desc: "Close dialogs and menus" },
    { keys: ["Tab"], desc: "Move forward between interactive elements" },
    { keys: ["Shift", "Tab"], desc: "Move backward" },
    { keys: ["Enter"], desc: "Activate buttons and links" },
    { keys: ["Space"], desc: "Toggle checkboxes, expand/collapse FAQ" },
    { keys: ["↑", "↓"], desc: "Adjust number inputs (browser default)" },
];

export default function KeyboardHelp() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") setOpen(false);
            // `?` requires Shift on most layouts; check by key
            if (e.key === "?" && !e.metaKey && !e.ctrlKey) {
                // Don't trigger inside inputs
                const tag = (e.target?.tagName || "").toLowerCase();
                if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
                e.preventDefault();
                setOpen((s) => !s);
            }
            if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
                const tag = (e.target?.tagName || "").toLowerCase();
                if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
                const toggle = document.querySelector('[data-testid="header-search-toggle"]');
                if (toggle) {
                    e.preventDefault();
                    toggle.click();
                    setTimeout(() => {
                        document.querySelector('[data-testid="header-search-input"]')?.focus();
                    }, 50);
                }
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    if (!open) return null;
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="kbd-title"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            data-testid="keyboard-help"
        >
            <button
                aria-label="Close keyboard help"
                onClick={() => setOpen(false)}
                className="absolute inset-0 bg-primary/40 backdrop-blur-sm"
            />
            <div className="relative bg-card rounded-2xl shadow-2xl max-w-md w-full border border-border">
                <div className="p-6 border-b border-border flex items-start justify-between">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent">
                            <Keyboard className="w-3.5 h-3.5" strokeWidth={1.5} /> Shortcuts
                        </div>
                        <h2 id="kbd-title" className="font-serif text-2xl text-primary mt-1">
                            Keyboard navigation
                        </h2>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        aria-label="Close"
                        className="h-11 w-11 grid place-items-center rounded-md hover:bg-muted"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <ul className="p-6 space-y-3">
                    {SHORTCUTS.map((s, i) => (
                        <li key={i} className="flex items-center justify-between gap-4">
                            <span className="text-sm text-foreground/80">{s.desc}</span>
                            <span className="flex gap-1">
                                {s.keys.map((k, j) => (
                                    <kbd
                                        key={j}
                                        className="px-2 py-1 rounded bg-muted border border-border font-mono text-[11px] text-primary"
                                    >
                                        {k}
                                    </kbd>
                                ))}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
